import { format } from 'date-fns';
import { resolveOutboxStepStatus } from '@/lib/outboxStatusDisplay';

const DELIVERED_STATUSES = new Set(['sent', 'replied', 'bounced']);

export function touchStageNumber(touch) {
  const n = touch?.touch_number ?? touch?.stage_no ?? touch?.order;
  const num = Number(n);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function resolveTouchStepStatus(touch) {
  if (!touch || typeof touch !== 'object') return 'pending';
  const explicit = resolveOutboxStepStatus(touch);
  if (explicit && explicit !== 'draft') return explicit;
  if (touch.replied_at) return 'replied';
  if (touch.bounced_at) return 'bounced';
  if (touch.actual_sent_at || touch.sent_at) return 'sent';
  if (explicit === 'draft') return 'draft_ready';
  return explicit || 'pending';
}

export function isDeliveredTouch(touch) {
  const status = resolveTouchStepStatus(touch);
  if (DELIVERED_STATUSES.has(status)) return true;
  return Boolean(touch?.actual_sent_at || touch?.sent_at);
}

export function countDeliveredTouches(touches) {
  if (!Array.isArray(touches)) return 0;
  const seen = new Set();
  let count = 0;
  for (const touch of touches) {
    const stage = touchStageNumber(touch);
    const key = stage ?? touch?.id ?? touch?.step_id;
    if (key != null && seen.has(key)) continue;
    if (key != null) seen.add(key);
    if (isDeliveredTouch(touch)) count += 1;
  }
  return count;
}

export function isCurrentlyEngaged(metrics) {
  if (!metrics) return false;
  const opens = Number(metrics.human_opens) || 0;
  const clicks = Number(metrics.human_clicks) || 0;
  const replies = Number(metrics.replies) || 0;
  return opens > 0 || clicks > 0 || replies > 0;
}

function formatTouchDateTime(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (!Number.isFinite(d.getTime())) return null;
    return format(d, 'MMM d, yyyy h:mm a');
  } catch {
    return null;
  }
}

function formatTouchDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (!Number.isFinite(d.getTime())) return null;
    return format(d, 'MMM d, yyyy');
  } catch {
    return null;
  }
}

/** Timeline label + styling from sequence_step status. */
export function getTouchTimelineDisplay(touch, { scheduledAt } = {}) {
  const status = resolveTouchStepStatus(touch);
  const sentAt = touch?.sent_at ?? touch?.actual_sent_at;
  const repliedAt = touch?.replied_at;
  const scheduled = scheduledAt ?? touch?.scheduled_send_at ?? touch?.scheduled_for;

  switch (status) {
    case 'sent': {
      const when = formatTouchDateTime(sentAt) || formatTouchDate(sentAt);
      return {
        status,
        label: when ? `Sent on ${when}` : 'Sent',
        className: 'text-green-600',
        strikethrough: false,
      };
    }
    case 'replied': {
      const when = formatTouchDateTime(repliedAt) || formatTouchDate(repliedAt);
      return {
        status,
        label: when ? `Replied on ${when}` : 'Replied',
        className: 'text-green-700 font-medium',
        strikethrough: false,
      };
    }
    case 'bounced':
      return {
        status,
        label: 'Bounced',
        className: 'text-red-600',
        strikethrough: false,
      };
    case 'scheduled': {
      const when = formatTouchDateTime(scheduled);
      return {
        status,
        label: when ? `Auto-send: ${when}` : 'Auto-send scheduled',
        className: 'text-blue-600',
        strikethrough: false,
      };
    }
    case 'draft_ready':
      return {
        status,
        label: 'Draft — awaiting your approval',
        className: 'text-gray-600',
        strikethrough: false,
      };
    case 'needs_review':
      return {
        status,
        label: 'Needs review (QA flagged)',
        className: 'text-amber-700',
        strikethrough: false,
      };
    case 'cancelled':
      return {
        status,
        label: 'Cancelled',
        className: 'text-gray-500',
        strikethrough: true,
      };
    case 'pending':
    default:
      return {
        status: status === 'pending' ? 'pending' : status,
        label: 'Not yet generated',
        className: 'text-gray-400',
        strikethrough: false,
      };
  }
}

function findBlockingTouch(touches) {
  if (!Array.isArray(touches) || touches.length === 0) return null;
  const sorted = [...touches].sort(
    (a, b) => (touchStageNumber(a) || 0) - (touchStageNumber(b) || 0),
  );
  return (
    sorted.find((t) => {
      const s = resolveTouchStepStatus(t);
      return s === 'needs_review' && !isDeliveredTouch(t);
    }) ||
    sorted.find((t) => {
      const s = resolveTouchStepStatus(t);
      return ['failed', 'error'].includes(s) && !isDeliveredTouch(t);
    }) ||
    null
  );
}

export function getEnrollmentBadge(progress, touches) {
  const total = Number(progress?.total_touches) || touches?.length || 0;
  const delivered = countDeliveredTouches(touches);

  if (total > 0 && delivered >= total) {
    return { label: 'Enrolled — Complete', className: 'text-[#5C6B7A]' };
  }

  const blocking = findBlockingTouch(touches);
  if (delivered === 0 && blocking) {
    const stage = touchStageNumber(blocking);
    const status = resolveTouchStepStatus(blocking);
    if (status === 'needs_review' && stage) {
      return {
        label: `Enrolled — Stuck (T${stage} needs review)`,
        className: 'text-amber-800',
      };
    }
    if (stage) {
      return {
        label: `Enrolled — Stuck (T${stage})`,
        className: 'text-amber-800',
      };
    }
    return { label: 'Enrolled — Stuck', className: 'text-amber-800' };
  }

  if (delivered === 0) {
    const first = [...(touches || [])].sort(
      (a, b) => (touchStageNumber(a) || 0) - (touchStageNumber(b) || 0),
    )[0];
    const firstStatus = first ? resolveTouchStepStatus(first) : '';
    if (['pending', 'draft_ready'].includes(firstStatus)) {
      const stage = touchStageNumber(first);
      return {
        label: stage ? `Enrolled — Stuck (T${stage})` : 'Enrolled — Stuck',
        className: 'text-amber-800',
      };
    }
  }

  return { label: 'Enrolled — In progress', className: 'text-[#0A1A2F]' };
}

export function mergeTouchesForDisplay(progress, sequenceTouches) {
  const byStage = new Map();

  for (const t of sequenceTouches || []) {
    const st = touchStageNumber(t);
    if (st != null) byStage.set(st, { ...t });
  }

  const progressTouches = [
    ...(progress?.completed_touches || []),
    ...(progress?.upcoming_touches || []),
  ];
  for (const t of progressTouches) {
    const st = touchStageNumber(t);
    if (st == null) continue;
    const prev = byStage.get(st);
    byStage.set(st, prev ? { ...prev, ...t } : { ...t });
  }

  if (byStage.size === 0) return [];

  return [...byStage.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, touch]) => touch);
}

export function formatDeliveredTouchesStat(delivered, total, enrolledAt, formatDate) {
  const enrolledSuffix =
    enrolledAt && formatDate ? ` (enrolled ${formatDate(enrolledAt)})` : '';
  return `${delivered} of ${total} touches delivered${enrolledSuffix}`;
}
